import React, { useEffect, useRef } from 'react';
import '../../styles/insightCards.css';

const HIGHLIGHT_DURATION_MS = 3000;

export interface InsightValueProps {
  /** Current value - when this changes (and parametersChanged), the highlight animation runs */
  value: number | undefined;
  /** Only highlight when user has adjusted parameters */
  parametersChanged?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps an insight value display and adds a highlight animation when the value
 * changes (e.g. after recalculation). Colocated - no document.getElementById.
 */
export default function InsightValue({
  value,
  parametersChanged = false,
  children,
  className = '',
}: InsightValueProps): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const prevValueRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!parametersChanged || ref.current == null) return;

    const prev = prevValueRef.current;
    const hasChanged =
      prev !== undefined &&
      value != null &&
      prev !== value;

    if (hasChanged) {
      ref.current.classList.add('value-changed');
      prevValueRef.current = value;
      const t = setTimeout(() => {
        ref.current?.classList.remove('value-changed');
      }, HIGHLIGHT_DURATION_MS);
      return () => clearTimeout(t);
    }

    prevValueRef.current = value;
  }, [value, parametersChanged]);

  return (
    <div ref={ref} className={`insight-value ${className}`.trim()}>
      {children}
    </div>
  );
}
